/* eslint-disable no-param-reassign */
import { fetchBaseQuery, buildCreateApi, coreModule, reactHooksModule, type FetchArgs, type BaseQueryApi } from '@reduxjs/toolkit/query/react';
import { PatchCollection } from '@reduxjs/toolkit/dist/query/core/buildThunks';

import { normalizeUrl } from '#helper/normalize-url';
import type { TStore } from '#core/store';
import { downloadFile } from '#helper/download-file';
import { EAdvertContentType } from '#core/const';
import { ECostType } from '#core/const/cost-type';
import { api as apiAuth } from '#adapter/api/auth';
import { mutex } from '#adapter/mutex';

import { slice as sliceUser } from '#component/user/slice';
import { editCampaignSlice } from '#route/campaigns/edit/slice';

import { environment } from '#environment';
import i18n from '#locale';

import { api as apiMain } from '../main';
import type { base as baseApiType } from '.';

/*
    Если у вас есть необходимость в использование контекста хранилища для createApi.
    Вы это можете сделать здесь по нижеприведенному примеру.

    https://redux-toolkit.js.org/rtk-query/usage/customizing-create-api

    Example:

    import { createDispatchHook, createSelectorHook, createStoreHook } from 'react-redux';

    export const createApi = buildCreateApi(
        coreModule(),
        reactHooksModule({
            useDispatch: createDispatchHook(context),
            useSelector: createSelectorHook(context),
            useStore   : createStoreHook(context)
        })
    );
*/
export const createApi = buildCreateApi(coreModule(), reactHooksModule());

/*
    Подробное описание допустимых параметров и примеров `fetchBaseQuery` можно посмотреть по ссылке.

    https://redux-toolkit.js.org/rtk-query/api/fetchBaseQuery#parameters
*/
export const baseQuery = (baseUrl: string) => {
    const query = fetchBaseQuery({
        baseUrl       : normalizeUrl(environment.hostApiMain + baseUrl),
        headers       : { 'cache-control': 'no-cache' },
        prepareHeaders: (headers, api) => {
            const { supplierOldID } = (api.getState() as TStore)[sliceUser.name];
            const token = localStorage.getItem(String(import.meta.env.APP_ACCESS_TOKEN_KEY));

            supplierOldID && headers.append('X-SupplierId', supplierOldID.toString());

            if(token) {
                headers.append('Authorizev3', token);
            }

            headers.append('Lang', i18n.language);

            return headers;
        }
    });

    return async (args: string | FetchArgs, api: BaseQueryApi, extraOptions: Record<PropertyKey, unknown>) => {
        await mutex.waitForUnlock();

        let result = await query(args, api, extraOptions);

        if(result.error?.status === 401) {
            localStorage.removeItem(String(import.meta.env.APP_ACCESS_TOKEN_KEY));

            if(!mutex.isLocked()) {
                const release = await mutex.acquire();

                try {
                    await api.dispatch(apiAuth.endpoints.check.initiate()).unwrap();

                    result = await query(args, api, extraOptions);
                } finally {
                    release();
                }
            } else {
                await mutex.waitForUnlock();

                result = await query(args, api, extraOptions);
            }
        }

        return result;
    };
};

/*
    Подробное описание взаимодействия с `enhanceEndpoints` можно посмотреть по ссылке.

    https://redux-toolkit.js.org/rtk-query/api/created-api/code-splitting#enhanceendpoints
*/
export const enhanceEndpoints = (api: typeof baseApiType) => {
    api.enhanceEndpoints({
        addTagTypes: ['auto-active', 'seacat-update-items', 'edit-status', 'search-update-items', 'catalog-update-items', 'carousel-auction-update-items', 'balance', 'active-banner'],
        endpoints  : {
            v2AutoAdvertIdDelnmPost: {
                async onQueryStarted(params, { dispatch, queryFulfilled }) {
                    const nms = params.body;

                    const patch = dispatch(
                        api.util.updateQueryData('v2AutoAdvertIdPlacementGet', {
                            path: {
                                advertID: params.path.advertID
                            }
                        }, (draft) => {
                            if(draft && nms?.length) {
                                draft.nms = draft.nms.filter((item) => !nms.includes(item.nm));
                            }
                        })
                    );

                    // @TODO: Костыль для обновления в карточке кампании. Убрать при обновлении карточки
                    const patchMain = dispatch(
                        apiMain.util.updateQueryData('campaignPlacementGet', {
                            path: {
                                id  : params.path.advertID,
                                type: 'auto'
                            }
                        }, (draft) => {
                            if('nms' in draft && nms?.length) {
                                draft.nms = draft.nms.filter((item: { nm: number }) => !nms.includes(item.nm));
                            }
                        })
                    );

                    try {
                        await queryFulfilled;
                    } catch{
                        patch.undo();
                        patchMain.undo();
                    }
                }
            },
            v2AutoAdvertIdSetcpmPost: {
                async onQueryStarted(params, { dispatch, queryFulfilled }) {
                    const patches = [];

                    if(params.body.nm === undefined) {
                        patches.push(
                            dispatch(
                                api.util.updateQueryData('v2AutoAdvertIdGetcpmGet', {
                                    path: {
                                        advertID: params.path.advertID
                                    }
                                }, (draft) => {
                                    return params.body.cpm || draft;
                                })
                            ),
                            dispatch(
                                api.util.updateQueryData('v2AutoAdvertIdPlacementGet', {
                                    path: {
                                        advertID: params.path.advertID
                                    }
                                }, (draft) => {
                                    Object.assign(draft, {
                                        raiseCPM: false
                                    });

                                    // eslint-disable-next-line @typescript-eslint/prefer-for-of
                                    for(let i = 0; i < draft.nms.length; i += 1) {
                                        draft.nms[i].cpm = params.body.cpm;
                                    }
                                })
                            )
                        );
                    } else {
                        patches.push(
                            dispatch(
                                api.util.updateQueryData('v2AutoAdvertIdPlacementGet', {
                                    path: {
                                        advertID: params.path.advertID
                                    }
                                }, (draft) => {
                                    Object.assign(draft, {
                                        raiseCPM: false
                                    });

                                    if(params.body.nm) {
                                        const index = draft.nms.findIndex((item) => item.nm === params.body.nm);

                                        if(index !== -1) {
                                            draft.nms[index].cpm = params.body.cpm;
                                        }
                                    }
                                })
                            )
                        );
                    }

                    try {
                        await queryFulfilled;
                    } catch{
                        patches.forEach((patch) => {
                            patch.undo();
                        });
                    }
                }
            },
            v5AutoSetcpmPost: {
                async onQueryStarted(params, { dispatch, queryFulfilled }) {
                    const patches = [];

                    patches.push(
                        dispatch(
                            api.util.updateQueryData('v5AutoGetCpInfoGet', {
                                query: {
                                    advert_id: params.body.advertId
                                }
                            }, (draft) => {
                                if(params.body.minCPO) {
                                    Object.assign(draft, {
                                        cpo: params.body.minCPO
                                    });
                                } else {
                                    Object.assign(draft, {
                                        cpm: params.body.cpm
                                    });
                                }

                                Object.assign(draft, {
                                    type: params.body.model
                                });
                            })
                        ),
                        dispatch(
                            api.util.updateQueryData('v2AutoAdvertIdPlacementGet', {
                                path: {
                                    advertID: String(params.body.advertId)
                                }
                            }, (draft) => {
                                Object.assign(draft, {
                                    raiseCPM: false
                                });

                                const costType = params.body.model as ECostType;

                                if(params.body.nm === undefined) {
                                    draft.nms.forEach((item) => ({ ...item, [costType]: params.body[costType] }));
                                } else {
                                    const nmItem = draft.nms.find((item) => item.nm === params.body.nm);

                                    if(nmItem) {
                                        nmItem[costType] = Number(params.body[costType]);
                                        nmItem.low_cr = false;
                                    }
                                }
                            })
                        )
                    );

                    try {
                        await queryFulfilled;
                    } catch{
                        patches.forEach((patch) => {
                            patch.undo();
                        });
                    }
                }
            },
            v5AutoMultiSetcpmPost: {
                async onQueryStarted(params, { dispatch, queryFulfilled }) {
                    const patch = dispatch(
                        api.util.updateQueryData('v2AutoAdvertIdPlacementGet', {
                            path: {
                                advertID: String(params.body.advertId)
                            }
                        }, (draft) => {
                            params.body?.nms?.forEach((nmId) => {
                                const index = draft.nms.findIndex((nmItem) => nmItem.nm === nmId);

                                if(params.body?.model) {
                                    const paymentModel = params.body.model as ECostType;
                                    const bet = params.body?.[paymentModel];

                                    if(bet) {
                                        draft.nms[index][paymentModel] = bet;
                                    }
                                }
                            });
                        })
                    );

                    try {
                        await queryFulfilled;
                    } catch{
                        patch.undo();
                    }
                }
            },
            v2AutoAdvertIdPlacementGet: {
                providesTags: ['auto-active', 'edit-status']
            },
            v2CarouselAuctionAdvertIdPlacementGet: {
                providesTags: ['carousel-auction-update-items']
            },
            v5SavePricePut: {
                invalidatesTags: ['catalog-update-items', 'carousel-auction-update-items']
            },
            v2SeacatPlacesEstimatePost: {
                invalidatesTags: ['seacat-update-items']
            },
            v2SeacatAdvertIdPlacementGet: {
                providesTags: ['seacat-update-items']
            },
            v2SeacatAdvertIdSetActivePost: {

                async onQueryStarted(params, { dispatch, queryFulfilled }) {
                    const patch = dispatch(
                        api.util.updateQueryData('v2SeacatAdvertIdPlacementGet', {
                            path: {
                                advertID: params.path.advertID
                            }
                        }, (draft) => {
                            draft.subjects = draft.subjects?.map((item) => {
                                if(item.subject === params.body?.[0]?.subject) {
                                    if(item.catalog) {
                                        item.catalog.active = params.body?.[0]?.catalog_is_active;
                                    }

                                    if(item.search) {
                                        item.search.active = params.body?.[0]?.search_is_active;
                                    }
                                }

                                return item;
                            });
                        }
                        )
                    );

                    try {
                        await queryFulfilled;
                    } catch{
                        patch.undo();
                    }
                }
            },
            v2SeacatAdvertIdDelnmPost: {
                async onQueryStarted(params, { dispatch, queryFulfilled }) {
                    const nms = params.body;

                    const patch = dispatch(
                        api.util.updateQueryData('v2SeacatAdvertIdPlacementGet', {
                            path: {
                                advertID: params.path.advertID
                            }
                        }, (draft) => {
                            if(draft && nms?.length) {
                                draft.subjects = draft.subjects?.map((subject) => {
                                    subject.nms = subject.nms?.filter((item) => !nms.includes(item.nm as number));

                                    return subject;
                                });
                                draft.subjects = draft.subjects?.filter((subject) => subject?.nms && subject?.nms.length > 0);
                                draft.nmsCount = (draft.nmsCount || 0) - nms.length;
                            }
                        })
                    );

                    // @TODO: Костыль для обновления в карточке кампании. Убрать при обновлении карточки
                    const patchMain = dispatch(
                        apiMain.util.updateQueryData('campaignPlacementGet', {
                            path: {
                                id  : params.path.advertID,
                                type: 'seacat'
                            }
                        }, (draft) => {
                            if('subjects' in draft && nms?.length) {
                                draft.subjects = (draft.subjects || []).map((subject) => {
                                    subject.nms = subject.nms?.filter((item) => !nms.includes(item.nm as number));

                                    return subject;
                                });
                                draft.subjects = draft.subjects.filter((subject) => subject.nms && subject.nms.length > 0);
                            }
                        })
                    );

                    try {
                        await queryFulfilled;
                    } catch{
                        patch.undo();
                        patchMain.undo();
                    }
                }
            },
            v2SeacatAdvertIdNewnmGet: {
                providesTags: ['seacat-update-items']
            },
            v2SearchAdvertIdPlacementGet: {
                providesTags: ['search-update-items']
            },
            v2CatalogAdvertIdPlacementGet: {
                providesTags: ['catalog-update-items']
            },
            v2AutoIdActivePost: {
                async onQueryStarted(params, { dispatch, queryFulfilled }) {
                    const patch = dispatch(
                        api.util.updateQueryData('v2AutoAdvertIdPlacementGet', {
                            path: {
                                advertID: params.path.id
                            }
                        }, (draft) => {
                            Object.assign(draft, { active: params.body });
                        })
                    );

                    try {
                        await queryFulfilled;
                    } catch{
                        patch.undo();
                    }
                }
            },
            v5WordsClustersGet: {
                query: (params) => ({
                    method: 'GET',
                    url   : '/v5/words-clusters',
                    params: {
                        advertID: params.query.advertID
                    },
                    responseHandler: (response) => (
                        downloadFile(response, i18n.t('components.keywords.tab-content.cluster.filename'))
                    )
                })
            },
            v5EventsExternalTrafficXlsGet: {
                query: (params) => ({
                    method: 'GET',
                    url   : '/v5/events-external-traffic/xls',
                    params: {
                        beginDate: params.query.beginDate,
                        endDate  : params.query.endDate
                    },
                    headers: {
                        'X-SupplierId': params?.header?.['X-SupplierId']
                    },
                    responseHandler: async (response) => (
                        downloadFile(
                            response,
                            'Внешний трафик.xlsx'
                        )
                    )
                })
            },
            v5SetNmCostPost: {
                async onQueryStarted(params, { dispatch, queryFulfilled, getState }) {
                    const { type } = (getState() as TStore)[editCampaignSlice.name];
                    const nms = params.body.costs;

                    if(type === EAdvertContentType.SearchCatalog) {
                        let patch!: PatchCollection;

                        try {
                            await queryFulfilled;

                            patch = dispatch(
                                api.util.updateQueryData('v2SeacatAdvertIdPlacementGet', {
                                    path: {
                                        advertID: String(params.body.advert_id)
                                    }
                                }, (draft) => {
                                    if(draft) {
                                        const { paymentModel } = draft;
                                        const newSubjects = draft.subjects?.map((item) => {
                                            const newNms = item.nms?.map((el) => {
                                                // @ts-ignore
                                                if(paymentModel === 'cpm' && nms[el.nm] !== undefined && nms[el.nm]?.cpm !== el.cpm) {
                                                    return {
                                                        ...el,
                                                        cpm: nms[el?.nm as number]?.cpm,
                                                        bet: {
                                                            type  : 'cpm' as 'cpm' | 'cpc',
                                                            amount: nms[el?.nm as number]?.cpm || draft.minCPM!
                                                        }
                                                    };
                                                }

                                                // @ts-ignore
                                                if(paymentModel === 'cpc' && nms[el.nm] !== undefined && nms[el.nm]?.cpc !== el.bet.amount) {
                                                    return {
                                                        ...el,
                                                        cpc: nms[el?.nm as number]?.cpc,
                                                        bet: {
                                                            type  : 'cpc' as 'cpm' | 'cpc',
                                                            amount: nms[el?.nm as number]?.cpc || draft.minCPC!
                                                        }
                                                    };
                                                }

                                                return el;
                                            });

                                            return {
                                                ...item,
                                                nms: newNms
                                            };
                                        });

                                        return {
                                            ...draft,
                                            subjects: newSubjects
                                        };
                                    }
                                })
                            );
                        } catch{
                            patch.undo();
                        }
                    }
                }
            },
            v5SupplierLanguagePost: {
                invalidatesTags: ['active-banner']
            }
        }
    });
};

/*
 Подробное описание взаимодействия с `injectEndpoints` можно посмотреть по ссылке.

 https://redux-toolkit.js.org/rtk-query/api/created-api/code-splitting#injectendpoints
 */
export const injectEndpoints = (api: typeof baseApiType) => {
    return api.injectEndpoints({
        endpoints: () => ({})
    });
};
